// src/components/ComingSoon.js
import React from 'react';

const ComingSoon = () => {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
      
      {/* Subtle grid background */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Animated floating elements */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white bg-opacity-5 animate-pulse"
            style={{
              width: `${40 + i * 15}px`,
              height: `${40 + i * 15}px`,
              left: `${5 + i * 12}%`,
              top: `${10 + (i % 4) * 25}%`,
              animationDelay: `${i * 0.7}s`,
              animationDuration: `${4 + i * 0.5}s`
            }}
          />
        ))}
      </div>

      {/* Main content container */}
      <div className="text-center max-w-4xl px-6 md:px-8 py-16 z-10 relative">
        
        {/* Logo/Brand Image */}
        <div className="mb-12 flex justify-center">
          <img 
            src="/logo-white.png" 
            alt="Infinity Development" 
            className="h-auto opacity-90 hover:opacity-100 transition-opacity duration-500"
            style={{ 
              maxHeight: '200px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>
        
        {/* Main heading */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-white mb-8 tracking-tight leading-tight">
          Infinity Development
        </h1>
        
        {/* Tagline */}
        <p className="text-xl md:text-2xl lg:text-3xl text-gray-300 font-light leading-relaxed mb-6">
          <span className="text-white font-medium">Sustainable.</span>{' '}
          <span className="text-white font-medium">Intelligent.</span>{' '}
          <span className="text-white font-medium">Infinite Real Estate.</span>
        </p>

        {/* Client-provided description */}
        <p className="text-lg md:text-xl text-gray-400 mb-16 font-normal max-w-2xl mx-auto">
          Infinity Development is shaping sustainable, high-value real estate across the Americas. 
          Full site coming soon.
          <br />
          <span className="text-base md:text-lg text-gray-500 mt-2 block">
            In the meantime, connect with us:
          </span>
        </p>

        {/* Contact buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6 mb-16">
          <a
            href="https://www.linkedin.com/in/zeus-hernandez/"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white bg-opacity-5 border border-white border-opacity-20 rounded-lg text-white font-medium hover:bg-opacity-10 hover:border-opacity-40 transition-all duration-300 hover:scale-105 backdrop-blur-sm"
          >
            <svg className="w-5 h-5 fill-current group-hover:scale-110 transition-transform duration-300" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            <span>LinkedIn</span>
          </a>
          
          <a
            href="mailto:zeus@infinitydevelopment.com"
            className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white bg-opacity-5 border border-white border-opacity-20 rounded-lg text-white font-medium hover:bg-opacity-10 hover:border-opacity-40 transition-all duration-300 hover:scale-105 backdrop-blur-sm"
          >
            <svg className="w-5 h-5 fill-current group-hover:scale-110 transition-transform duration-300" viewBox="0 0 24 24">
              <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-.904.732-1.636 1.636-1.636h.473L12 10.71l9.891-6.889h.473c.904 0 1.636.732 1.636 1.636z"/>
            </svg>
            <span>Email Us</span>
          </a>
        </div>

        {/* Subtle divider */}
        <div className="w-24 h-px bg-gradient-to-r from-transparent via-white via-opacity-30 to-transparent mx-auto mb-8"></div>
        
        {/* Footer */}
        <p className="text-gray-500 text-sm md:text-base">
          &copy; 2025 Infinity Development. All rights reserved.
        </p>

        {/* Hidden admin access */}
        <div className="mt-8">
          <button
            onClick={() => {
              const password = prompt('Enter admin access code:');
              if (password === 'infinity2025') {
                localStorage.setItem('infinityAdminMode', 'true');
                window.location.reload();
              }
            }}
            className="text-gray-800 hover:text-gray-600 text-xs transition-colors duration-300 opacity-30 hover:opacity-60"
            title="Admin Access"
          >
            •••
          </button>
        </div>
      </div>

      {/* Subtle bottom glow */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-96 h-32 bg-white bg-opacity-5 blur-3xl rounded-full"></div>
    </div>
  );
};

export default ComingSoon;
